import React from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Input } from './ui';

export const SOCIAL_PLATFORMS = [
  { key: 'website', label: 'Website', mark: '↗', placeholder: 'https://example.com' },
  { key: 'facebook', label: 'Facebook', mark: 'f', placeholder: 'https://facebook.com/...' },
  { key: 'instagram', label: 'Instagram', mark: '◎', placeholder: 'https://instagram.com/...' },
  { key: 'youtube', label: 'YouTube', mark: '▶', placeholder: 'https://youtube.com/@...' },
  { key: 'linkedin', label: 'LinkedIn', mark: 'in', placeholder: 'https://linkedin.com/in/...' },
  { key: 'x', label: 'X', mark: '𝕏', placeholder: 'https://x.com/...' },
  { key: 'tiktok', label: 'TikTok', mark: '♪', placeholder: 'https://tiktok.com/@...' },
  { key: 'whatsapp', label: 'WhatsApp', mark: '◉', placeholder: 'https://wa.me/...' },
  { key: 'telegram', label: 'Telegram', mark: '➤', placeholder: 'https://t.me/...' },
];

const PLATFORM_MAP = Object.fromEntries(SOCIAL_PLATFORMS.map((platform) => [platform.key, platform]));

export function cleanSocialLinks(links = {}) {
  return Object.fromEntries(Object.entries(links || {}).filter(([, url]) => String(url || '').trim()));
}

export function SocialLinksEditor({ value = {}, onChange }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {SOCIAL_PLATFORMS.map((platform) => (
        <Field key={platform.key} label={t(platform.label)}>
          <Input
            type="url"
            value={value?.[platform.key] || ''}
            placeholder={platform.placeholder}
            onChange={(event) => onChange({ ...value, [platform.key]: event.target.value })}
            aria-label={t('{{platform}} profile URL', { platform: platform.label })}
          />
        </Field>
      ))}
    </div>
  );
}

export default function SocialLinks({ links, className = '', maxItems }) {
  const entries = Object.entries(cleanSocialLinks(links));
  const visibleEntries = maxItems ? entries.slice(0, maxItems) : entries;
  if (visibleEntries.length === 0) return null;

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {visibleEntries.map(([key, url]) => {
        const platform = PLATFORM_MAP[key] || { label: key, mark: '↗' };
        return (
          <a key={key} href={url} target="_blank" rel="noopener noreferrer" title={platform.label} aria-label={platform.label} className="flex h-9 w-9 items-center justify-center rounded-full border border-surface-200 bg-white text-xs font-bold text-gray-600 shadow-sm transition hover:-translate-y-0.5 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700">
            {platform.mark}
          </a>
        );
      })}
    </div>
  );
}
